// @ts-nocheck
// app/about.tsx — Editorial about page (The Career Editorial)
// =====================================================================

import React, { useCallback, useState } from 'react';
import { useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { Linking, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import * as Haptics from 'expo-haptics';
import { ArrowLeft, Instagram, Mail } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_FAINT, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  Body, DoubleRule, EditorialButton, EditorialFooter, Eyebrow,
  Headline, Kicker, Numeral, Paper, PageHero, Section, Title, useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const SANS_BOLD = theme.fonts.bold;
const IS_WEB = Platform.OS === 'web';

const INSTAGRAM_URL = 'https://www.instagram.com/kursi_il?igsh=MXI4aWpwbzV0YW16Yw==';
const SUPPORT_EMAIL = 'hello@kursiaii.com';

const PRINCIPLES = [
  {
    n: '01',
    title: 'שלוש התאמות. לא יותר.',
    body: 'הצמצום הוא הערך, לא הכמות. אנחנו מאמינים שעורך טוב יודע מה להשאיר בחוץ — וזו בדיוק הדרך שבה Kursi עובדת.',
  },
  {
    n: '02',
    title: 'בלי ספונסרים בראש הרשימה',
    body: 'התאמות מבוססות על מה שכתבתם — לא על מי שמשלם הכי הרבה. נקודה. זאת ההבטחה הראשונה, האחרונה והיחידה שאנחנו מבטיחים.',
  },
  {
    n: '03',
    title: 'AI שיודע גם להגיד "לא יודע"',
    body: 'אם אין לנו התאמה טובה — נגיד לכם. אם הטיעון של המכללה רעוע — נכתוב לכם. אנחנו לא בקטע של הבטחות שווא.',
  },
  {
    n: '04',
    title: 'עיתון, לא קטלוג',
    body: 'Kursi מתעדכנת באותו קצב שבו השוק זז. אנחנו לא מאגר סטטי — אנחנו מערכת חיה של עורכים, סוקרים ומפתחים שעובדים בשבילכם.',
  },
];

const TEAM_NOTES = [
  { side: 'בצד שלכם', body: 'אנחנו לא מוכרים לכם קורס. אנחנו עוזרים לכם לבחור — וזה ההבדל הכי גדול בין Kursi לכל פלטפורמה אחרת.' },
  { side: 'בצד של השוק', body: 'אנחנו עובדים עם מנטורים, מכללות ופרילנסרים — אבל אף אחד מהם לא יכול לקנות לעצמו מקום בראש ההמלצות.' },
  { side: 'בצד של ישראל', body: 'אנחנו עיתון קריירה ישראלי. בעברית. עם ההקשרים המקומיים. אין לנו תרגום ממאגר אמריקאי.' },
];

const FAQ = [
  {
    q: 'איך אתם מרוויחים אם לא דרך ספונסרים?',
    a: 'אנחנו מקבלים עמלת רכישה ממכללות אחרי שתלמיד התקבל ושילם — אבל זה לא משפיע על מי עולה בהתאמות. המכללות יודעות שזה התנאי, וזה לא נושא למו"מ.',
  },
  {
    q: 'אם חיפשתי משהו וקיבלתי תוצאה לא רלוונטית, מה לעשות?',
    a: 'תכתבו לנו ב-' + SUPPORT_EMAIL + '. כל "התאמה רעה" שאנחנו מקבלים נכנסת לתהליך עדכון של המודל. זה איך שאנחנו משתפרים.',
  },
  {
    q: 'אתם נותנים יעוץ קריירה?',
    a: 'לא יעוץ עומק. אבל אנחנו מציעים שאלון התאמה ל-2-3 כיוונים אם עוד לא יודעים מה מחפשים, ומפנים למנטור מתאים כשצריך.',
  },
  {
    q: 'מה זה KURSI LEARN?',
    a: 'מוצר הלמידה החדש שלנו: מעלים חומר לימוד (PDF, וידאו, מאמרים), מקבלים מסלול AI עם תרגול, תיקון טעויות ובדיקת מוכנות למבחן. זה לא חלק מההתאמה — זה כלי נפרד.',
  },
];

export default function AboutScreen() {
  const router = useRouter();
  const { isDesktop } = useEditorialBreakpoint();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const hapticLight = useCallback(() => {
    if (!IS_WEB) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
  }, []);
  const openInstagram = useCallback(async () => {
    hapticLight();
    try {
      const supported = await Linking.canOpenURL(INSTAGRAM_URL);
      if (supported) await Linking.openURL(INSTAGRAM_URL);
    } catch {}
  }, [hapticLight]);
  const openEmail = useCallback(async () => {
    hapticLight();
    try {
      const url = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('שלום מ-Kursi')}`;
      const supported = await Linking.canOpenURL(url);
      if (supported) await Linking.openURL(url);
    } catch {}
  }, [hapticLight]);

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>אודות · Kursi · עיתון הקריירה והלמידה של ישראל</title>
          <meta name="description" content="מי אנחנו, איך אנחנו עובדים, ומה ההבטחה שלנו לכם. Kursi — שלוש התאמות. לא יותר. בלי ספונסרים. בעברית." />
          <link rel="canonical" href="https://kursiaii.com/about" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1100}>
          <PageHero
            kicker="גיליון אודות · עיתון Kursi"
            title={<>הסיפור,{'\n'}השיטה,{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>וההבטחה.</Text></>}
            lede={<>Kursi הוקמה מתוך תסכול: שוק הקורסים בישראל ענק, איכותי בחלקו וזרוק לגמרי בחלק אחר — ואין לאף אחד את הזמן או הכלים לסנן את הרעש. אז בנינו את הכלי. </>}
            meta="נכתב במערכת Kursi · עודכן 2025"
          />
        </Section>

        {/* ─── PRINCIPLES ───────────────────────────────────────────────── */}
        <Section py="xxl" px="md" max={1240}>
          <Eyebrow>ארבעה עקרונות</Eyebrow>
          <View style={{ height: 14 }} />
          <Headline level={2}>איך אנחנו חושבים על הבחירה שלכם.</Headline>
          <View style={{ height: 40 }} />

          <View style={{ flexDirection: isDesktop ? 'row-reverse' : 'column', flexWrap: 'wrap', gap: 0 } as any}>
            {PRINCIPLES.map((p, i) => (
              <View
                key={i}
                style={{
                  width: isDesktop ? '50%' : '100%',
                  paddingHorizontal: isDesktop ? (i % 2 === 0 ? 0 : 32) : 0,
                  paddingVertical: 28,
                  borderTopWidth: 1, borderTopColor: INK,
                  ...(isDesktop && i % 2 === 0 ? { borderLeftWidth: 1, borderLeftColor: INK, paddingLeft: 32 } : {}),
                }}
              >
                <Numeral size={isDesktop ? 88 : 64}>{p.n}</Numeral>
                <View style={{ height: 14 }} />
                <Title level={3}>{p.title}</Title>
                <View style={{ height: 10 }} />
                <Text style={s.body}>{p.body}</Text>
              </View>
            ))}
          </View>
        </Section>

        <Section py="md" px="md" max={1240}><DoubleRule /></Section>

        {/* ─── PULL QUOTE ─────────────────────────────────────────────── */}
        <Section py="xxl" px="md" max={900}>
          <View style={{ alignItems: 'center' }}>
            <Text style={s.quoteMark}>“</Text>
            <Text style={s.quote}>
              קורסים זה לא מוצר שאתה קונה. זה החלטה שאתה חי איתה שנה.
              אנחנו לא רוצים לעזור לכם <Text style={{ fontStyle: 'italic' as any, color: ACCENT }}>למצוא קורס</Text>.
              אנחנו רוצים לעזור לכם <Text style={{ fontStyle: 'italic' as any, color: ACCENT }}>להחליט נכון</Text>.
            </Text>
          </View>
        </Section>

        {/* ─── EDITORIAL POSITIONING ───────────────────────────────────── */}
        <View style={{ backgroundColor: INK }}>
          <Section py="xxl" px="md" max={1240}>
            <Eyebrow color={ACCENT}>איפה אנחנו עומדים</Eyebrow>
            <View style={{ height: 14 }} />
            <Text style={s.darkHeadline}>שלוש זוויות. אותה ערך.</Text>
            <View style={{ height: 36 }} />
            <View style={{ flexDirection: isDesktop ? 'row-reverse' : 'column', gap: 0 } as any}>
              {TEAM_NOTES.map((t, i) => (
                <View key={i} style={[s.darkCol, { flex: 1, borderRightWidth: isDesktop && i > 0 ? 1 : 0, borderRightColor: ACCENT, paddingRight: isDesktop && i > 0 ? 28 : 0, paddingLeft: isDesktop && i < TEAM_NOTES.length - 1 ? 28 : 0 } as any]}>
                  <Text style={s.darkColKicker}>{t.side}</Text>
                  <View style={{ height: 14 }} />
                  <Text style={s.darkColBody}>{t.body}</Text>
                </View>
              ))}
            </View>
          </Section>
        </View>

        {/* ─── FAQ ───────────────────────────────────────────────────── */}
        <Section py="xxl" px="md" max={1000}>
          <Eyebrow>שאלות נשאלות</Eyebrow>
          <View style={{ height: 14 }} />
          <Headline level={2}>מה שאתם תוהים — ואנחנו עונים.</Headline>
          <View style={{ height: 32 }} />

          <View style={{ borderTopWidth: 1, borderTopColor: INK }}>
            {FAQ.map((f, i) => {
              const open = openFaq === i;
              return (
                <Pressable
                  key={i}
                  onPress={() => setOpenFaq(open ? null : i)}
                  style={s.faqItem}
                  accessibilityRole="button"
                >
                  <View style={{ flexDirection: 'row-reverse', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 } as any}>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row-reverse', gap: 12, alignItems: 'baseline' } as any}>
                        <Text style={s.faqIndex}>{String(i + 1).padStart(2, '0')}</Text>
                        <Text style={s.faqQ}>{f.q}</Text>
                      </View>
                      {open && (
                        <View style={{ marginRight: 28, marginTop: 14 }}>
                          <Text style={s.faqA}>{f.a}</Text>
                        </View>
                      )}
                    </View>
                    <Text style={s.faqToggle}>{open ? '–' : '+'}</Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </Section>

        {/* ─── CONTACT ──────────────────────────────────────────────── */}
        <Section py="xxl" px="md" max={1240}>
          <View style={s.contactBlock}>
            <View style={{ flex: 1 }}>
              <Eyebrow>צרו קשר</Eyebrow>
              <View style={{ height: 14 }} />
              <Headline level={2}>יש שאלה. יש משוב. כותבים.</Headline>
              <View style={{ height: 16 }} />
              <Text style={s.body}>אנחנו קוראים הכל. תוך 48 שעות נחזיר תשובה — לרוב פחות.</Text>
            </View>
            <View style={{ flexDirection: 'column', gap: 14 } as any}>
              <Pressable onPress={openEmail} style={s.contactRow}>
                <View style={s.contactIcon}><Mail color={INK} size={18} /></View>
                <View>
                  <Text style={s.contactLabel}>אימייל</Text>
                  <Text style={s.contactValue}>{SUPPORT_EMAIL}</Text>
                </View>
                <ArrowLeft color={INK} size={16} style={{ marginRight: 'auto' as any }} />
              </Pressable>
              <Pressable onPress={openInstagram} style={s.contactRow}>
                <View style={s.contactIcon}><Instagram color={INK} size={18} /></View>
                <View>
                  <Text style={s.contactLabel}>אינסטגרם</Text>
                  <Text style={s.contactValue}>@kursi_il</Text>
                </View>
                <ArrowLeft color={INK} size={16} style={{ marginRight: 'auto' as any }} />
              </Pressable>
            </View>
          </View>
        </Section>

        {/* ─── CTA ───────────────────────────────────────────────────── */}
        <Section py="xl" px="md" max={1240}>
          <View style={s.endCta}>
            <Headline level={2} color={PAPER}>מוכנים? נתחיל.</Headline>
            <View style={{ height: 14 }} />
            <Text style={[s.body, { color: PAPER_SOFT }]}>שורת חיפוש אחת. שלוש התאמות. בעברית. בלי טפסים ארוכים.</Text>
            <View style={{ height: 24 }} />
            <EditorialButton
              label="התחל התאמה עכשיו"
              onPress={() => router.push('/chat')}
              variant="accent" size="lg"
              trailing={<ArrowLeft size={16} color={PAPER} />}
            />
          </View>
        </Section>

        <EditorialFooter
          columns={[
            { title: 'מקטעים', items: [
              { label: 'הקטלוג המלא', href: '/catalog' },
              { label: 'תחומי לימוד', href: '/fields' },
              { label: 'שאלון התאמה', href: '/quiz' },
            ]},
            { title: 'המוצר', items: [
              { label: 'איך זה עובד', href: '/how-it-works' },
              { label: 'המתודולוגיה', href: '/methodology' },
              { label: 'שאלות נפוצות', href: '/faq' },
            ]},
          ]}
          onNavigate={(href) => router.push(href as any)}
        />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  body: { fontFamily: SANS, fontSize: 17, lineHeight: 28, color: INK_SOFT, textAlign: 'right' },

  quoteMark: { fontFamily: SERIF_BLACK, fontSize: 120, color: ACCENT, lineHeight: 96, height: 80 },
  quote: { fontFamily: SERIF, fontSize: IS_WEB ? 30 : 22, lineHeight: IS_WEB ? 44 : 32, color: INK, textAlign: 'center', letterSpacing: -0.4, fontStyle: 'italic' as any, maxWidth: 820 },

  darkHeadline: { fontFamily: SERIF_BLACK, fontSize: IS_WEB ? 52 : 32, color: PAPER, letterSpacing: -1.2, lineHeight: IS_WEB ? 56 : 36, textAlign: 'right' },
  darkCol: { paddingVertical: 8 },
  darkColKicker: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.8, textTransform: 'uppercase' as any, textAlign: 'right' },
  darkColBody: { fontFamily: SERIF, fontSize: 18, color: PAPER_SOFT, lineHeight: 28, textAlign: 'right' },

  faqItem: {
    paddingVertical: 22,
    borderBottomWidth: 1, borderBottomColor: INK,
  },
  faqIndex: { fontFamily: MONO, fontSize: 13, color: ACCENT, letterSpacing: 1.4 },
  faqQ: { fontFamily: SERIF_BOLD, fontSize: 22, color: INK, letterSpacing: -0.4, flex: 1, textAlign: 'right' },
  faqA: { fontFamily: SANS, fontSize: 16, lineHeight: 26, color: INK_SOFT, textAlign: 'right' },
  faqToggle: { fontFamily: SERIF_BLACK, fontSize: 32, color: INK, lineHeight: 32, width: 24, textAlign: 'center' },

  contactBlock: {
    flexDirection: IS_WEB ? 'row-reverse' : 'column' as any,
    gap: 40,
    padding: 36,
    borderWidth: 1, borderColor: INK,
    backgroundColor: PAPER_SOFT,
  } as any,
  contactRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 14,
    paddingVertical: 14, paddingHorizontal: 18,
    borderWidth: 1, borderColor: INK,
    backgroundColor: PAPER,
  } as any,
  contactIcon: {
    width: 40, height: 40,
    borderWidth: 1, borderColor: INK,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: PAPER_SOFT,
  },
  contactLabel: { fontFamily: MONO, fontSize: 10, color: INK_MUTED, letterSpacing: 1.5, textTransform: 'uppercase' as any, textAlign: 'right' },
  contactValue: { fontFamily: SERIF_BOLD, fontSize: 18, color: INK, textAlign: 'right' },

  endCta: {
    backgroundColor: INK,
    padding: 48,
    alignItems: 'flex-start',
  },
});
